import { useState, useEffect } from 'react';
import { sourcesApi, DataSource, FetchRun } from '../api/client';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { RequirePermission } from '../components/auth/RoleGuard';
import { useRole } from '../contexts/RoleContext';

interface FetchResult {
  runId: number;
  sourceId: number;
  sourceName: string;
  status: 'success' | 'failed';
  totalRecords: number;
  newRecords: number;
  updatedRecords: number;
  duration: number;
  errorMessage?: string;
}

function DataSourcesContent() {
  const { can } = useRole();
  const canFetch = can('sources:fetch');

  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchingIds, setFetchingIds] = useState<Set<number>>(new Set());
  const [fetchResults, setFetchResults] = useState<Record<number, FetchResult>>({});
  const [runs, setRuns] = useState<Record<number, FetchRun[]>>({});
  const [expandedSource, setExpandedSource] = useState<number | null>(null);

  useEffect(() => {
    loadSources();
  }, []);

  async function loadSources() {
    try {
      setLoading(true);
      setError(null);
      const response = await sourcesApi.getSources();
      setSources(response?.sources || []);
    } catch (err) {
      setError('Failed to load data sources');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleFetch(sourceId: number) {
    setFetchingIds(prev => new Set(prev).add(sourceId));
    setFetchResults(prev => {
      const next = { ...prev };
      delete next[sourceId];
      return next;
    });

    try {
      const result = await sourcesApi.triggerFetch(sourceId) as FetchResult;
      setFetchResults(prev => ({ ...prev, [sourceId]: result }));
      // Reload sources to get updated last_fetch_at
      await loadSources();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fetch failed';
      setFetchResults(prev => ({
        ...prev,
        [sourceId]: {
          runId: 0,
          sourceId,
          sourceName: '',
          status: 'failed',
          totalRecords: 0,
          newRecords: 0,
          updatedRecords: 0,
          duration: 0,
          errorMessage,
        },
      }));
    } finally {
      setFetchingIds(prev => {
        const next = new Set(prev);
        next.delete(sourceId);
        return next;
      });
    }
  }

  async function handleFetchAll() {
    const activeSourceIds = sources.filter(s => s.active).map(s => s.id);
    activeSourceIds.forEach(id => {
      setFetchingIds(prev => new Set(prev).add(id));
    });

    try {
      await sourcesApi.triggerFetchAll();
      await loadSources();
    } catch (err) {
      setError('Failed to fetch all sources');
    } finally {
      setFetchingIds(new Set());
    }
  }

  async function loadRuns(sourceId: number) {
    if (expandedSource === sourceId) {
      setExpandedSource(null);
      return;
    }

    try {
      const { runs: sourceRuns } = await sourcesApi.getRuns(sourceId);
      setRuns(prev => ({ ...prev, [sourceId]: sourceRuns }));
      setExpandedSource(sourceId);
    } catch (err) {
      console.error('Failed to load runs', err);
    }
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  }

  function formatDuration(ms: number) {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <Button onClick={loadSources} className="mt-2">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-font-primary">Data Sources</h1>
          <p className="text-font-secondary mt-1">
            {canFetch ? 'Manage and fetch data from external sources' : 'View configured data sources'}
          </p>
        </div>
        {canFetch && (
          <Button
            onClick={handleFetchAll}
            disabled={fetchingIds.size > 0}
          >
            {fetchingIds.size > 0 ? 'Fetching...' : 'Fetch All Active'}
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {(sources || []).map(source => {
          const isFetching = fetchingIds.has(source.id);
          const result = fetchResults[source.id];
          const sourceRuns = runs[source.id] || [];
          const isExpanded = expandedSource === source.id;

          return (
            <div
              key={source.id}
              className="bg-white rounded-lg border border-card-border shadow-sm overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-font-primary">
                        {source.name}
                      </h3>
                      <Badge variant={source.active ? 'success' : 'default'}>
                        {source.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="info">{source.region}</Badge>
                      <Badge variant="default">{source.source_type}</Badge>
                    </div>
                    <p className="text-sm text-font-secondary mt-1 font-mono truncate">
                      {source.api_url}
                    </p>
                    <p className="text-sm text-font-secondary mt-2">
                      Last fetched: {formatDate(source.last_fetch_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadRuns(source.id)}
                    >
                      {isExpanded ? 'Hide History' : 'History'}
                    </Button>
                    {canFetch && (
                      <Button
                        onClick={() => handleFetch(source.id)}
                        disabled={isFetching || !source.active}
                        size="sm"
                      >
                        {isFetching ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent" />
                            Fetching...
                          </span>
                        ) : (
                          'Fetch Now'
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Fetch Result */}
                {result && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    result.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    {result.status === 'success' ? (
                      <div className="text-green-800">
                        <p className="font-medium">Fetch completed successfully</p>
                        <div className="flex gap-4 mt-1 text-sm">
                          <span>Total: {result.totalRecords}</span>
                          <span>New: {result.newRecords}</span>
                          <span>Updated: {result.updatedRecords}</span>
                          <span>Duration: {formatDuration(result.duration)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-red-800">
                        <p className="font-medium">Fetch failed</p>
                        <p className="text-sm mt-1">{result.errorMessage}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fetch History */}
              {isExpanded && sourceRuns.length > 0 && (
                <div className="border-t border-card-border bg-gray-50 p-4">
                  <h4 className="text-sm font-semibold text-font-secondary mb-3">Fetch History</h4>
                  <div className="space-y-2">
                    {sourceRuns.slice(0, 5).map(run => (
                      <div
                        key={run.id}
                        className="flex items-center justify-between text-sm bg-white p-2 rounded border"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant={run.status === 'success' ? 'success' : run.status === 'failed' ? 'danger' : 'warning'}>
                            {run.status}
                          </Badge>
                          <span className="text-font-secondary">
                            {formatDate(run.started_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-font-secondary">
                          {run.status === 'success' && (
                            <>
                              <span>Total: {run.total_records}</span>
                              <span>New: {run.new_records}</span>
                            </>
                          )}
                          {run.error_message && (
                            <span className="text-red-600 truncate max-w-xs" title={run.error_message}>
                              {run.error_message}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isExpanded && sourceRuns.length === 0 && (
                <div className="border-t border-card-border bg-gray-50 p-4">
                  <p className="text-sm text-font-secondary">No fetch history available</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DataSources() {
  return (
    <RequirePermission permission="sources:view">
      <DataSourcesContent />
    </RequirePermission>
  );
}
